import { getRequest, patchRequest, postRequest } from 'helpers/axios';
import { stringify } from 'qs';
import { getCurrentSubscription } from 'services/subscriptionService';
import onBoardingService from 'services/onBoardingService';

// ##### ##### ##### ##### #####   GET   ##### ##### ##### ##### ##### //
export const getUsers = (search, page = 1) => {
  const queryParams = {
    page,
    'search[name_cont]': search,
  };

  return getRequest(`system_users?${stringify(queryParams)}`);
};

const getUser = async () => {
  const response = await getRequest('profile');
  return response?.data;
};

export const getAccessLevels = () => {
  return getRequest('access_levels');
};

// ##### ##### ##### ##### #####   POST   ##### ##### ##### ##### ##### //
export const confirmEmail = async (token) => {
  try {
    const response = await postRequest('confirm_email', { token });

    // After successful confirmation, log the user in
    if (response?.data?.user) {
      // Get JWT token from response headers (where Devise JWT puts it)
      const jwtToken =
        response.headers?.authorization || response.headers?.Authorization;

      if (!jwtToken) {
        throw new Error('Authentication failed: No token received');
      }

      // Remove 'Bearer ' prefix if present
      const cleanToken = jwtToken.startsWith('Bearer ')
        ? jwtToken.substring(7)
        : jwtToken;

      // Create user object with token
      const userData = {
        ...response.data.user,
        token: cleanToken,
      };

      // Store in localStorage to log them in (same way your app handles login)
      localStorage.setItem('user', JSON.stringify(userData));

      // Fetch subscription data to prevent redirect to onboarding
      try {
        const [currentSubscriptionRes, licenseData] = await Promise.all([
          getCurrentSubscription(),
          onBoardingService.getLicenseData(),
        ]);

        const { data: subscriptionData } = currentSubscriptionRes || {};
        const { data: licenseDataResult } = licenseData || {};

        // Update user data with subscription and license information
        if (subscriptionData || licenseDataResult) {
          const updatedUserData = {
            ...userData,
            subscriptions: subscriptionData,
            license: licenseDataResult,
          };
          localStorage.setItem('user', JSON.stringify(updatedUserData));
        }
      } catch (subscriptionError) {
        console.warn('Failed to fetch subscription data:', subscriptionError);
        // Don't fail the email confirmation if subscription fetch fails
      }

      return response;
    } else {
      throw new Error('No user data received');
    }
  } catch (error) {
    throw error;
  }
};

const updateUser = (body) => {
  return patchRequest('signup', body, {
    'content-type': 'multipart/form-data',
  }).then((response) => {
    const oldUser = getLocalStorageUser();
    if (response?.data) {
      const payload = {
        ...response?.data?.user,
        token: oldUser.token,
      };
      updateLocalStorageUser(payload);

      return response;
    }
  });
};

const updateProfile = (body) => {
  return patchRequest('update_user', body, {
    'content-type': 'multipart/form-data',
  }).then((response) => {
    const oldUser = getLocalStorageUser();
    if (response?.data) {
      const payload = {
        ...response?.data?.user,
        token: oldUser.token,
      };
      updateLocalStorageUser(payload);

      return response;
    }
  });
};

const updateUserPreferences = async (preferences) => {
  const response = await patchRequest('update_user_preferences', {
    user: {
      preferences,
    },
  });
  return response?.data;
};

const getLocalStorageUser = () => {
  return JSON.parse(localStorage.getItem('user'));
};

const updateLocalStorageUser = (user) => {
  localStorage.setItem('user', JSON.stringify(user));
};

const userService = {
  updateUser,
  getUser,
  updateUserPreferences,
  getLocalStorageUser,
  updateLocalStorageUser,
  updateProfile,
};

export default userService;
